import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Divider,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { getCurrencySymbol } from '../../utils/currency';

const getStatusColor = (status) => {
  const colors = {
    completed: 'success',
    pending: 'warning',
    processing: 'info',
    failed: 'error',
    cancelled: 'default'
  };
  return colors[status] || 'default';
};

const getStatusIcon = (status) => {
  const icons = {
    completed: <CheckCircleIcon />,
    pending: <ScheduleIcon />,
    processing: <ScheduleIcon />,
    failed: <ErrorIcon />,
    cancelled: <ErrorIcon />
  };
  return icons[status] || <ScheduleIcon />;
};

/**
 * Composant pour afficher le récapitulatif du payout de compensation de dommages
 * Affiché uniquement au propriétaire dans la section résolution d'un litige résolu en sa faveur
 */
const DamageCompensationPayoutSummary = ({ payout, reservationCurrency }) => {
  const { t, i18n } = useTranslation('disputes');
  const dateLocale = i18n?.language && i18n.language.startsWith('fr') ? fr : enUS;

  if (!payout) {
    return null;
  }

  const currencySymbol = getCurrencySymbol(payout.currency);
  const reservationCurrencySymbol = getCurrencySymbol(reservationCurrency || payout.currency);
  const amount = parseFloat(payout.amount || 0);
  const netAmount = parseFloat(payout.net_amount || 0);
  const commissionRate = parseFloat(payout.commission_rate || 0);
  const commissionAmount = amount - netAmount;

  return (
    <Card
      sx={{
        mt: 2,
        bgcolor: 'background.paper',
        borderLeft: 6,
        borderLeftColor: 'primary.main',
        borderRadius: 2,
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 8px 20px rgba(0,0,0,0.6)'
            : '0 14px 40px rgba(33,150,243,0.08)'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {t('compensationPayout.title', 'Compensation versée')}
          </Typography>
        </Box>

        <Alert severity="info" icon={<ReceiptIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t(
              'compensationPayout.description',
              'Le montant de compensation pour dommages a été traité et transféré vers votre compte Stripe Connect.'
            )}
          </Typography>
        </Alert>

        <Grid container spacing={2}>
          {/* Statut du payout */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('compensationPayout.status', 'Statut')}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  icon={getStatusIcon(payout.status)}
                  label={t(`payoutStatus.${payout.status}`, payout.status)}
                  color={getStatusColor(payout.status)}
                  size="small"
                />
              </Box>
            </Box>
          </Grid>

          {/* Montant brut */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('compensationPayout.grossAmount', 'Montant brut')}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {amount.toFixed(2)} {reservationCurrencySymbol}
              </Typography>
            </Box>
          </Grid>

          {/* Commission */}
          {commissionRate > 0 && commissionAmount > 0 && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('compensationPayout.commission', 'Commission plateforme')} ({(commissionRate * 100).toFixed(1)}%)
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  - {commissionAmount.toFixed(2)} {currencySymbol}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Montant net */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('compensationPayout.netAmount', 'Montant net versé')}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {netAmount.toFixed(2)} {currencySymbol}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Référence de transfert */}
          {payout.transfer_reference && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('compensationPayout.reference', 'Référence de transfert')}
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {payout.transfer_reference}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* ID de transfert Stripe */}
          {payout.stripe_transfer_id && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('compensationPayout.stripeTransferId', 'ID Stripe Transfer')}
                </Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                  {payout.stripe_transfer_id}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Date de traitement */}
          {payout.processed_at && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('compensationPayout.processedAt', 'Date de traitement')}
                </Typography>
                <Typography variant="body2">
                  {format(new Date(payout.processed_at), 'dd MMMM yyyy à HH:mm', { locale: dateLocale })}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Date de création */}
          {payout.created_at && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('compensationPayout.createdAt', 'Date de création')}
                </Typography>
                <Typography variant="body2">
                  {format(new Date(payout.created_at), 'dd MMMM yyyy à HH:mm', { locale: dateLocale })}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Raison d'échec si applicable */}
          {payout.status === 'failed' && payout.failure_reason && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {t('compensationPayout.failureReason', 'Raison de l\'échec')}:
                </Typography>
                <Typography variant="body2">
                  {payout.failure_reason}
                </Typography>
              </Alert>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default DamageCompensationPayoutSummary;
